import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Lightning, Leaf, Quotes } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import LeadForm from "../components/LeadForm";
import api from "../lib/api";

const CATEGORIES = ["all", "residential", "commercial", "industrial"];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? projects : projects.filter(p => p.category === filter);

  return (
    <div data-testid="projects-page">
      <SEO page="projects" titleFallback="Solar Projects — Orax Solar"/>

      <section className="pt-32 pb-10 bg-[#F9F8F6]">
        <div className="container-custom">
          <div className="overline mb-3">CASE STUDIES</div>
          <h1 className="h1 max-w-3xl">500+ solar installations across Maharashtra</h1>
          <p className="text-lg text-[#2D4739] mt-4 max-w-2xl">
            Real projects with real savings. Browse our portfolio by category.
          </p>

          <div className="flex flex-wrap gap-2 mt-8">
            {CATEGORIES.map(c => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                  filter === c ? "bg-[#0A1F15] text-white" : "bg-white border border-[#E5E3DB] text-[#2D4739] hover:border-[#0A1F15]"
                }`}
                data-testid={`project-filter-${c}`}
              >
                {c === "all" ? "All Projects" : c.charAt(0).toUpperCase() + c.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 bg-[#F9F8F6]">
        <div className="container-custom">
          {loading ? (
            <div className="text-center py-20 text-[#5C7368]">Loading projects...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-[#5C7368]">No projects in this category yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {filtered.map((p, i) => (
                <motion.article
                  key={p.id}
                  initial={{opacity:0,y:20}}
                  whileInView={{opacity:1,y:0}}
                  viewport={{once:true}}
                  transition={{delay: i*0.05}}
                  className="bg-white rounded-3xl overflow-hidden border border-[#E5E3DB] card-lift"
                  data-testid={`project-card-${p.id}`}
                >
                  <div className="grid grid-cols-2 aspect-[2/1]">
                    <div className="relative">
                      {p.before_image && <img src={p.before_image} alt="Before" className="w-full h-full object-cover"/>}
                      <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-black/70 text-white text-xs font-bold">BEFORE</div>
                    </div>
                    <div className="relative">
                      {p.after_image && <img src={p.after_image} alt="After" className="w-full h-full object-cover"/>}
                      <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-[#135934] text-white text-xs font-bold">AFTER</div>
                    </div>
                  </div>
                  <div className="p-7">
                    <div className="text-xs font-bold uppercase tracking-wider text-[#135934] mb-2">{p.category}</div>
                    <h2 className="font-display font-bold text-2xl mb-3 text-[#0A1F15]">{p.title}</h2>
                    <p className="text-[#2D4739] mb-4">{p.description}</p>

                    <div className="grid grid-cols-3 gap-3 pb-4 border-b border-[#E5E3DB]">
                      <div>
                        <div className="text-xs text-[#5C7368] flex items-center gap-1"><MapPin size={12}/> Location</div>
                        <div className="font-semibold text-[#0A1F15]">{p.location}</div>
                      </div>
                      <div>
                        <div className="text-xs text-[#5C7368] flex items-center gap-1"><Lightning size={12}/> Size</div>
                        <div className="font-semibold text-[#0A1F15]">{p.system_size}</div>
                      </div>
                      <div>
                        <div className="text-xs text-[#5C7368] flex items-center gap-1"><Leaf size={12}/> Savings</div>
                        <div className="font-semibold text-[#135934]">{p.savings}</div>
                      </div>
                    </div>

                    {p.customer_feedback && (
                      <div className="pt-4 flex gap-3">
                        <Quotes weight="fill" size={20} className="text-[#FDB813] shrink-0"/>
                        <div>
                          <p className="text-sm text-[#2D4739] italic">"{p.customer_feedback}"</p>
                          {p.customer_name && <p className="text-xs text-[#5C7368] mt-1">— {p.customer_name}</p>}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <h2 className="h2 mb-4">Want to join our success stories?</h2>
            <p className="text-lg text-[#2D4739]">Get a free solar consultation for your home or business.</p>
          </div>
          <div className="md:col-span-6">
            <div className="bg-[#F9F8F6] rounded-2xl p-7 border border-[#E5E3DB]">
              <LeadForm source="projects"/>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
